import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import z from 'zod'

import { CheckboxGroup } from '@/components/CheckboxGroup'
import { Button } from '@/components/elements/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/elements/form'
import { Modal } from '@/components/Modal'
import { grantStudentCertificate } from '@/services/entity_student_certificates.service'
import { toast } from '@/utils/helpers'

import EventSlideoutView from '../../Calendar/EventSlideoutView'

/* eslint-disable react/react-in-jsx-scope */
function GrantCertificateModal({ certificate, onClose, entityId, name }) {
  // State
  const [showEventSlideout, setShowEventSlideout] = useState(false)

  // Data
  const certificateData = certificate.certificate
  const hasEvent = !!certificate.event
  const hasRoster = hasEvent ? !!certificate.event?.roster : true
  const canGrant = !hasEvent || hasRoster
  const hasTraining = certificate.trainingStatus !== null

  const options = (certificateData.proficiencyQuestions || [])
    .filter((q) => typeof q?.questionText === 'string' && q.questionText.trim() !== '')
    .map((question) => ({
      id: question.id,
      label: question.questionText,
    }))

  // Form - only require proficiency questions if training is required
  const schema = z.object({
    proficiencyQuestions: hasTraining
      ? z
          .array(z.string())
          .min(
            options.length,
            'The student must be proficient in all areas to be granted the course.',
          )
      : z.array(z.string()).optional(),
  })
  const form = useForm({ resolver: zodResolver(schema) })

  // Data processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const grantCertificate = useMutation({
    mutationFn: (data) => grantStudentCertificate(entityId, certificate.id, data),
    onSuccess: () => {
      handleSuccess('Certificate granted successfully')
      queryClient.invalidateQueries({ queryKey: ['accounts', 'students', 'certificates'] })
      onClose()
    },
    onError: (err) => {
      handleError(err.message)
    },
  })

  const onSubmit = (data) => {
    grantCertificate.mutate(data)
  }

  if (showEventSlideout && certificate.event) {
    return (
      <EventSlideoutView
        viewEvent={certificate.event}
        studentId={certificate.student.id}
        canManage
        resetAndClose={() => {
          // Invalidate queries to refresh the certificate data with updated event/roster
          queryClient.invalidateQueries({ queryKey: ['accounts', 'students', 'certificates'] })
          // Close the entire modal flow so it can be reopened with fresh data
          onClose()
        }}
      />
    )
  }

  return (
    <Modal
      title={`Grant ${certificateData.name} certificate to ${name}`}
      size="lg"
      content={
        <div>
          {!canGrant && (
            <div className="bg-warning/10 border-warning mb-4 rounded-md border p-4">
              <p className="text-warning font-semibold">Roster Required</p>
              <p className="mt-2 text-sm">
                A roster must be uploaded to the event before certificates can be granted.
              </p>
              <Button
                variant="link"
                className="text-primary mt-2 h-auto p-0 text-sm underline"
                onClick={() => setShowEventSlideout(true)}
              >
                View {certificate.event?.name} to upload roster
              </Button>
            </div>
          )}
          <Form {...form}>
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
              {hasTraining && options.length > 0 && (
                <FormField
                  control={form.control}
                  name="proficiencyQuestions"
                  render={({ field }) => (
                    <FormItem className="text-2xl">
                      <FormControl>
                        <CheckboxGroup id="profiencyQuestions" options={options} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {!hasTraining && (
                <p className="text-muted-foreground text-sm">
                  This course does not require hands-on training proficiency verification.
                </p>
              )}
            </form>
            <div className="py-4">
              <Button
                type="submit"
                disabled={!form.formState.isValid || !canGrant}
                onClick={form.handleSubmit(onSubmit)}
              >
                Grant
              </Button>
            </div>
          </Form>
        </div>
      }
      setOpen={onClose}
    />
  )
}

export default GrantCertificateModal

GrantCertificateModal.propTypes = {
  certificate: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
}
